import { makeApi, Zodios, type ZodiosInstance, type ZodiosOptions } from '@zodios/core'
import { z } from 'zod'

const queryFilters = z
  .object({ accessGroups: z.array(z.string()), ids: z.array(z.string()) })
  .partial()
  .passthrough()
  .optional()
const TaskBase = z
  .object({
    name: z.string().min(3).max(200),
    body: z.string().min(10).max(50000),
    teaser: z.string().min(10).max(200),
    ownerId: z
      .string()
      .min(1)
      .max(50)
      .regex(/^[a-zA-Z0-9_-]+$/)
      .optional(),
    allowedWeapons: z
      .array(z.enum(['SQL', 'Python']))
      .min(1)
      .max(2)
      .optional(),
    difficulty: z.enum(['Easy', 'Intermediate', 'Hard', 'Expert']),
    description: z.string().min(20).max(10000),
    defaultQuery: z.string().min(1).max(2000),
    seedFactor: z.number(),
    feedbackPayload: z.string().min(2).optional(),
    dataBank: z.string().min(2),
    accessGroups: z
      .array(
        z
          .string()
          .min(1)
          .max(50)
          .regex(/^[a-zA-Z0-9_-]+$/)
      )
      .min(1)
      .max(10),
    proficiencies: z
      .array(
        z.enum([
          'joins',
          'dataSanitization',
          'anomalyDetection',
          'timeSeriesAnalysis',
          'windowFunctions',
          'aggregation',
        ])
      )
      .optional(),
    solutionPlaceholder: z.string().optional(),
    availabilityWeight: z.number().optional(),
    deleted: z.boolean().optional(),
    sharedDBName: z.string().optional(),
    customQueryTimeout: z.number().gte(1).lte(255).optional(),
    slug: z.string().optional(),
    coverImageUrl: z.string().optional(),
    coverFocusY: z.number().gte(0).lte(100).optional(),
  })
  .passthrough()
const Task = TaskBase.and(z.object({ id: z.string() }).passthrough())
const CountResponse = z.object({ count: z.number() }).passthrough()
const TaskErrorCode = z.enum(['taskNotFound', 'badRequest', 'invalidTaskId'])
const ErrorResponse = z
  .object({ error: z.string(), errorCode: TaskErrorCode.optional() })
  .passthrough()
const TaskSearchResult = z
  .object({ id: z.string(), name: z.string() })
  .passthrough()
const AdminMigrateTaskRequest = z
  .object({ taskId: z.string(), sharedDBName: z.string() })
  .passthrough()
const AdminMigrateTaskResponse = z
  .object({
    success: z.boolean(),
    challengeId: z.string().optional(),
    steps: z.array(
      z
        .object({
          step: z.string(),
          status: z.string(),
          error: z.string().optional(),
        })
        .passthrough()
    ),
  })
  .passthrough()
const TaskUpdate = TaskBase
const TaskSortFieldEnum = z.enum([
  'id',
  'name',
  'difficulty',
  'ownerId',
  'allowedWeapons',
  'seedFactor',
  'availabilityWeight',
])
const SortDirectionEnum = z.enum(['asc', 'desc'])

export const schemas = {
  queryFilters,
  TaskBase,
  Task,
  CountResponse,
  TaskErrorCode,
  ErrorResponse,
  TaskSearchResult,
  AdminMigrateTaskRequest,
  AdminMigrateTaskResponse,
  TaskUpdate,
  TaskSortFieldEnum,
  SortDirectionEnum,
}

const endpoints: ReturnType<typeof makeApi> = makeApi([
  {
    method: 'get',
    path: '/',
    alias: 'get',
    description: `Retrieves all tasks with optional filtering, sorting and pagination`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'queryFilters',
        type: 'Query',
        schema: queryFilters,
      },
      {
        name: 'sortField',
        type: 'Query',
        schema: z
          .enum([
            'id',
            'name',
            'difficulty',
            'ownerId',
            'allowedWeapons',
            'seedFactor',
            'availabilityWeight',
          ])
          .optional(),
      },
      {
        name: 'sortDirection',
        type: 'Query',
        schema: z.enum(['asc', 'desc']).optional(),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.number().int().gte(1).optional(),
      },
      {
        name: 'offset',
        type: 'Query',
        schema: z.number().int().gte(0).optional(),
      },
      {
        name: 'select',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z.array(Task),
  },
  {
    method: 'post',
    path: '/',
    alias: 'post',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: TaskBase,
      },
    ],
    response: Task,
  },
  {
    method: 'get',
    path: '/:_id',
    alias: 'get_id',
    requestFormat: 'json',
    parameters: [
      {
        name: '_id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: Task,
    errors: [
      {
        status: 404,
        description: `Task not found`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'put',
    path: '/:_id',
    alias: 'put_id',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: TaskBase,
      },
      {
        name: '_id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: Task,
    errors: [
      {
        status: 404,
        description: `Task not found`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'delete',
    path: '/:_id',
    alias: 'delete_id',
    requestFormat: 'json',
    parameters: [
      {
        name: '_id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 404,
        description: `Task not found`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'post',
    path: '/admin/actions/migrate-task-to-shared-db',
    alias: 'adminActionsMigrateTaskToSharedDB',
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: AdminMigrateTaskRequest,
      },
    ],
    response: AdminMigrateTaskResponse,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'get',
    path: '/count',
    alias: 'getCount',
    requestFormat: 'json',
    response: z.object({ count: z.number() }).passthrough(),
  },
  {
    method: 'get',
    path: '/search',
    alias: 'getSearch',
    requestFormat: 'json',
    parameters: [
      {
        name: 'q',
        type: 'Query',
        schema: z.string().min(1),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.number().int().lte(50).optional().default(20),
      },
    ],
    response: z.array(TaskSearchResult),
  },
])

export const api: ZodiosInstance<typeof endpoints> = new Zodios(endpoints)

export function createApiClient(baseUrl: string, options?: ZodiosOptions): ZodiosInstance<typeof endpoints> {
  return new Zodios(baseUrl, endpoints, options)
}
